import React, { useState, useEffect, useMemo } from 'react';
import {
  FaSearch,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaChartArea,
  FaTable,
  FaPlus,
  FaArrowLeft,
  FaArrowRight,
  FaEye,
  FaCog,
  FaFilter,
  FaRefresh,
  FaTachometerAlt,
  FaRegFileAlt
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import ReportChart from '../reports/charts/ReportChart';
import { ChartRenderer } from '../reports/charts';
import {
  normalizeReportChartForDisplay,
} from '../reports/charts/transformWidgetChart';
import {
  getChartTypeDisplayIcon,
  getChartTypeDisplayLabel,
} from '../reports/charts/chartDisplayUtils';
import { splitLoadedReportFields } from '../reports/matrix/matrixReportConfig';
import { useReportCurrency } from '../reports/hooks/useReportCurrency';
import { buildChartDatasetForPreview } from '../../utils/reportChartData';
import { coerceReportFieldsArray } from '../reports/charts/chartPicklistColors';
import {
  getKpiDateFieldCandidates,
  getKpiMetricFieldCandidates,
  normalizeReportFieldForKpi,
  KPI_APPEARANCE_DEFAULTS,
} from '../../utils/kpiWidgetData';
import './css/WidgetBuilder.css';

const REPORT_COLUMN_OPTIONS = [
  { value: 3, label: '3 Columns (25%)' },
  { value: 4, label: '4 Columns (33%)' },
  { value: 6, label: '6 Columns (50%)' },
  { value: 8, label: '8 Columns (67%)' },
  { value: 9, label: '9 Columns (75%)' },
  { value: 12, label: '12 Columns (Full Width)' }
];

const DASHBOARD_COLUMN_VALUES = Array.from({ length: 12 }, (_, index) => index + 1);
const ROW_OPTIONS = Array.from({ length: 25 }, (_, index) => index + 1);

const getDashboardColumnLabel = (value) => {
  if (value === 1) return '1 Column';
  if (value === 6) return '6 Columns (Default)';
  if (value === 12) return '12 Columns (Full Width)';
  return `${value} Columns`;
};

const getRowOptionLabel = (value) => {
  if (value === 1) return '1 Row';
  if (value === 4) return '4 Rows (Default)';
  return `${value} Rows`;
};

const KPI_AGGREGATIONS = ['SUM', 'COUNT', 'AVG', 'MIN', 'MAX'];
const KPI_COMPARISONS = ['LAST_MONTH', 'LAST_WEEK', 'LAST_YEAR', 'NONE'];
const KPI_FORMATS = ['currency', 'number', 'percentage'];

const DEFAULT_KPI_WIDGET_CONFIG = {
  label: '',
  metric_field: '',
  aggregation: 'SUM',
  comparison: 'LAST_MONTH',
  date_field: '',
  format: 'currency',
  show_trend: true,
  decimal_places: 1,
  background_color: KPI_APPEARANCE_DEFAULTS.background_color,
  title_color: KPI_APPEARANCE_DEFAULTS.title_color,
  value_color: KPI_APPEARANCE_DEFAULTS.value_color,
  positive_color: KPI_APPEARANCE_DEFAULTS.positive_color,
  negative_color: KPI_APPEARANCE_DEFAULTS.negative_color,
};

const getDashboardDisplayLabel = (dashboard) =>
  dashboard?.label || dashboard?.name || 'Dashboard';

const getReportDisplayLabel = (report) =>
  report?.report_label || report?.name || 'Report';

const cloneDashboardSelection = (dashboard) =>
  dashboard ? { ...dashboard } : null;

const getConfigStep = (typeValue, includeFullValue) => {
  if (typeValue === 'kpi') {
    return 4;
  }
  if (typeValue === 'dashboard') {
    return 3;
  }

  if (typeValue === 'report') {
    return 4;
  }

  return includeFullValue ? 3 : 4;
};

const getKpiConfigValidationError = (kpiCfg) => {
  const cfg = { ...DEFAULT_KPI_WIDGET_CONFIG, ...(kpiCfg || {}) };
  if (!cfg.metric_field) {
    return 'Please select a metric field for the KPI.';
  }
  if (cfg.comparison && cfg.comparison !== 'NONE' && !cfg.date_field) {
    return 'Please select a date field for period comparison.';
  }
  return null;
};

const WidgetBuilder = ({
  isOpen,
  onClose,
  dashboardId,
  onAddWidget,
  onPreviewWidget,
  editingWidget
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [widgetType, setWidgetType] = useState('report');
  const [dashboards, setDashboards] = useState([]);
  const [filteredDashboards, setFilteredDashboards] = useState([]);
  const [dashboardSearchTerm, setDashboardSearchTerm] = useState('');
  const [dashboardsLoading, setDashboardsLoading] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportCharts, setReportCharts] = useState([]);
  const [selectedChart, setSelectedChart] = useState(null);
  const [showChartBuilder, setShowChartBuilder] = useState(false);
  const [includeFullReport, setIncludeFullReport] = useState(false);

  // Report data processing states (following ReportDataViewer pattern)
  const [reportInfo, setReportInfo] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedColumnGroups, setSelectedColumnGroups] = useState([]);
  const [summarizedFields, setSummarizedFields] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [groupingInfo, setGroupingInfo] = useState(null);
  const [reportConfig, setReportConfig] = useState(null);
  const [loadedReportFields, setLoadedReportFields] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const {
    reportConversionBaseCode,
    reportConversionLookup,
    multiCurrencyEnabled,
  } = useReportCurrency();
  const [widgetConfig, setWidgetConfig] = useState({
    name: '',
    chart_type: 'bar',
    column_span: 6, // Default to half of 12-column grid
    row_span: 4, // Default height
    refresh_rate: 300, // 5 minutes
    show_filters: true,
    show_export: true,
    sort_order: '', // Sort order number
    chart_config: {},
    widget_config: { ...DEFAULT_KPI_WIDGET_CONFIG },
    type: 'report'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearReportPreviewState = () => {
    setSelectedReport(null);
    setSelectedChart(null);
    setReportCharts([]);
    setSelectedFields([]);
    setSummarizedFields({});
    setSelectedGroups([]);
    setSelectedColumnGroups([]);
    setPreviewData([]);
    setGroupingInfo(null);
    setReportConfig(null);
    setLoadedReportFields([]);
  };

  useEffect(() => {
    if (!isOpen) {
      resetBuilder();
      return;
    }

    resetBuilder();

    const editingTypeRaw = editingWidget ? (editingWidget.type || editingWidget.chart_type || '') : 'report';
    const editingNorm = String(editingTypeRaw).toLowerCase();
    const resolvedType =
      editingNorm === 'dashboard'
        ? 'dashboard'
        : editingNorm === 'kpi'
          ? 'kpi'
          : 'report';
    const isChartWidget = editingTypeRaw === 'chart';

    setWidgetType(resolvedType);
    setIncludeFullReport(resolvedType === 'report' ? !isChartWidget : false);

    if (resolvedType === 'dashboard') {
      loadDashboards();
    } else {
      loadReports();
    }

    if (editingWidget) {
      const editingKpiConfig = {
        ...DEFAULT_KPI_WIDGET_CONFIG,
        ...(editingWidget.widget_config || {}),
      };
      if (editingNorm === 'kpi' && !editingKpiConfig.label) {
        editingKpiConfig.label = editingWidget.name || '';
      }
      setWidgetConfig({
        name:
          editingNorm === 'kpi'
            ? editingKpiConfig.label || editingWidget.name || ''
            : editingWidget.name || '',
        chart_type: editingWidget.chart_type || 'bar',
        column_span: editingWidget.column_span || 6,
        row_span: editingWidget.row_span || 4,
        refresh_rate: editingWidget.refresh_rate || 300,
        show_filters: editingWidget.show_filters !== undefined ? editingWidget.show_filters : true,
        show_export: editingWidget.show_export !== undefined ? editingWidget.show_export : true,
        sort_order: editingWidget.sort_order || '',
        chart_config: editingWidget.chart_config || {},
        widget_config: editingKpiConfig,
      });

      if (resolvedType === 'dashboard') {
        setSelectedDashboard({
          id: editingWidget.parent_id,
          label: editingWidget.dashboard_label || editingWidget.label,
          name: editingWidget.dashboard_label || editingWidget.name,
          description: editingWidget.dashboard_description || ''
        });
        setCurrentStep(3);
      } else {
        const reportIdForWidget = editingWidget.parent_id || editingWidget.report_id;
        setSelectedReport({
          id: reportIdForWidget,
          report_label: editingWidget.report_label || editingWidget.name?.split(' - ')[0] || 'Selected Report',
          name: editingWidget.report_label || editingWidget.name?.split(' - ')[0] || 'Selected Report'
        });

        loadReportData(reportIdForWidget);

        if (editingNorm === 'kpi') {
          setCurrentStep(3);
        } else if (editingTypeRaw === 'report') {
          setIncludeFullReport(true);
          setSelectedChart(null);
          setCurrentStep(getConfigStep('report', true));
        } else {
          setIncludeFullReport(false);
          const chartData = editingWidget.chart_config || editingWidget.selectedChart || {};
          setSelectedChart(chartData);
          setCurrentStep(getConfigStep('report', false));
        }
      }
    } else {
      setCurrentStep(1);
    }
  }, [isOpen, editingWidget]);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm]);

  useEffect(() => {
    filterDashboards();
  }, [dashboards, dashboardSearchTerm, dashboardId]);

  useEffect(() => {
    const maxStep = getConfigStep(widgetType, includeFullReport);
    if (currentStep > maxStep) {
      setCurrentStep(maxStep);
    }
  }, [widgetType, includeFullReport]);

  useEffect(() => {
    if (widgetType === 'dashboard' && selectedDashboard && dashboards.length) {
      const match = dashboards.find(d => d.id === selectedDashboard.id);
      if (match && (match.name !== selectedDashboard.name || match.label !== selectedDashboard.label)) {
        setSelectedDashboard(match);
      }
    }
  }, [widgetType, dashboards, selectedDashboard]);

  const resetBuilder = () => {
    setCurrentStep(1);
    setWidgetType('report');
    setSelectedDashboard(null);
    setDashboards([]);
    setFilteredDashboards([]);
    setDashboardSearchTerm('');
    setSelectedReport(null);
    setSelectedChart(null);
    setShowChartBuilder(false);
    setIncludeFullReport(true);
    setSearchTerm('');
    setSelectedFields([]);
    setSummarizedFields({});
    setSelectedGroups([]);
    setSelectedColumnGroups([]);
    setPreviewData([]);
    setGroupingInfo(null);
    setReportConfig(null);
    setLoadedReportFields([]);
    setWidgetConfig({
      name: '',
      chart_type: 'bar',
      column_span: 6, // Default to half of 12-column grid
      row_span: 4, // Default height
      refresh_rate: 300,
      show_filters: true,
      show_export: true,
      sort_order: '', // Sort order number
      chart_config: {},
      type: 'report'
    });
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const reportApi = new DataApi('reports'); // Fixed: use 'reports' (plural) to match backend route
      console.log('Loading reports from API...');
      const response = await reportApi.fetchAll();

      console.log('Reports API response:', response.data);

      if (response.data.success) {
        setReports(response.data.data);
        console.log('Reports loaded successfully:', response.data.data.length, 'reports');
      } else {
        setError('Failed to load reports');
        console.error('API returned success: false', response.data);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Error loading reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboards = async () => {
    try {
      setDashboardsLoading(true);
      setError(null);
      const dashboardApi = new DataApi('dashboard');
      console.log('Loading dashboards from API...');
      const response = dashboardApi.fetchAll ? await dashboardApi.fetchAll() : await dashboardApi.get('/');
      const data = response?.data?.data || response?.data || [];
      setDashboards(data);
      console.log('Dashboards loaded successfully:', data.length);
    } catch (err) {
      console.error('Error loading dashboards:', err);
      setError('Error loading dashboards. Please try again.');
    } finally {
      setDashboardsLoading(false);
    }
  };

  const filterReports = () => {
    if (!searchTerm.trim()) {
      setFilteredReports(reports);
    } else {
      const q = searchTerm.toLowerCase();
      const filtered = reports.filter((report) => {
        const label = getReportDisplayLabel(report).toLowerCase();
        return (
          label.includes(q) ||
          (report.description && report.description.toLowerCase().includes(q))
        );
      });
      setFilteredReports(filtered);
    }
  };

  const filterDashboards = () => {
    const eligible = dashboards.filter(
      (dashboard) => !dashboardId || String(dashboard.id) !== String(dashboardId)
    );
    if (!dashboardSearchTerm.trim()) {
      setFilteredDashboards(eligible);
    } else {
      const q = dashboardSearchTerm.toLowerCase();
      const filtered = eligible.filter((dashboard) => {
        const label = getDashboardDisplayLabel(dashboard).toLowerCase();
        return (
          label.includes(q) ||
          (dashboard.description && dashboard.description.toLowerCase().includes(q))
        );
      });
      setFilteredDashboards(filtered);
    }
  };


  const handleReportSelect = async (report) => {
    console.log('Report selected:', report);
    const reportDetails = {
      id: report.id,
      report_label: report.report_label || report.name,
      name: report.report_label || report.name,
      description: report.description
    };
    setSelectedReport(reportDetails);
    setError(null);
    setIncludeFullReport(false);
    if (widgetType === 'kpi') {
      const defaultLabel = `${reportDetails.report_label} KPI`;
      setWidgetConfig(prev => ({
        ...prev,
        name: prev.name || prev.widget_config?.label || defaultLabel,
        chart_type: 'kpi',
        type: 'kpi',
        widget_config: {
          ...DEFAULT_KPI_WIDGET_CONFIG,
          ...(prev.widget_config || {}),
          label: prev.widget_config?.label || prev.name || defaultLabel,
        },
      }));
      try {
        await loadReportData(report.id);
      } catch (err) {
        console.error('Error loading report data:', err);
        setError('Error loading report data. Please try again.');
      }
      setCurrentStep(3);
      return;
    }

    setWidgetConfig(prev => ({
      ...prev,
      name: prev.name || `${reportDetails.report_label} Widget`,
      chart_type: 'bar',
      type: 'chart'
    }));

    try {
      await loadReportData(report.id);
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Error loading report data. Please try again.');
    }

    setCurrentStep(3);
  };

  const handleKpiConfigChange = (field, value) => {
    setError(null);
    setWidgetConfig((prev) => {
      const nextConfig = {
        ...(prev.widget_config || DEFAULT_KPI_WIDGET_CONFIG),
        [field]: value,
      };
      if (field === 'aggregation') {
        const candidates = getKpiMetricFieldCandidates(loadedReportFields, value);
        const currentMetric = nextConfig.metric_field;
        if (
          currentMetric &&
          !candidates.some((f) => f.field_name === currentMetric)
        ) {
          nextConfig.metric_field = '';
        }
      }
      const next = { ...prev, widget_config: nextConfig };
      if (field === 'label') {
        next.name = typeof value === 'string' ? value : '';
      }
      return next;
    });
  };

  const kpiAggregation =
    (widgetConfig.widget_config || {}).aggregation || 'SUM';

  const kpiNumericFields = useMemo(
    () => getKpiMetricFieldCandidates(loadedReportFields, kpiAggregation),
    [loadedReportFields, kpiAggregation]
  );

  const kpiDateFields = useMemo(
    () => getKpiDateFieldCandidates(loadedReportFields),
    [loadedReportFields]
  );

  // Load report data following ReportDataViewer pattern
  const loadReportData = async (reportId) => {
    setPreviewLoading(true);
    setError(null);

    try {
      // First, get the report configuration (same as ReportDataViewer)
      const reportBuilderApi = new DataApi('reportbuilder');
      const reportResponse = await reportBuilderApi.get(`/${reportId}`);

      if (!reportResponse?.data?.success) {
        throw new Error('Failed to load report configuration');
      }

      const report = reportResponse.data.data;
      console.log('WidgetBuilder - Loaded report for viewer:', report);

      setReportConfig(report.config || null);
      const normalizedReportFields = coerceReportFieldsArray(report.fields)
        .map(normalizeReportFieldForKpi)
        .filter(Boolean);
      setLoadedReportFields(normalizedReportFields);

      // Set report information (same as ReportDataViewer)
      setReportInfo({
        id: report.id,
        name: report.name,
        report_label: report.report_label,
        description: report.description,
        object_name: report.object_name,
        object_label: report.object_label,
        report_type: report.report_type,
        is_public: report.is_public,
        filter_logic: report.filter_logic,
        filter_custom_logic: report.filter_custom_logic,
        report_objects: report.report_objects || []
      });

      // Process report fields and groups (same as ReportDataViewer)
      const reportFieldsList = coerceReportFieldsArray(report.fields);
      if (reportFieldsList.length > 0) {
        const fields = reportFieldsList.map(field => ({
          id: field.field_config_id,
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          is_aggregated: field.is_aggregated,
          aggregate_function: field.aggregate_function,
          is_grouped: field.is_grouped,
          sort_order: field.sort_order
        }));

        // Helper function to convert database aggregation values to dropdown values
        const convertAggregationValue = (dbValue) => {
          const mapping = {
            'average': 'avg',
            'sum': 'sum',
            'count': 'count',
            'min': 'min',
            'max': 'max'
          };
          return mapping[dbValue] || dbValue;
        };

        const { rowGroups, columnGroups, selectedFieldsList, summarized } =
          splitLoadedReportFields(
            reportFieldsList.map((field) => ({
              ...field,
              field_config_id: field.field_config_id,
              group_axis: field.group_axis,
            })),
            convertAggregationValue
          );

        setSelectedFields(selectedFieldsList);
        setSelectedGroups(rowGroups);
        setSelectedColumnGroups(columnGroups);
        setSummarizedFields(summarized);

        // Load charts from the report data (same as ReportDataViewer)
        console.log('WidgetBuilder - Report charts data:', report.charts);
        console.log('WidgetBuilder - Is charts array?', Array.isArray(report.charts));
        console.log('WidgetBuilder - Charts length:', report.charts ? report.charts.length : 'undefined');

        if (report.charts && Array.isArray(report.charts) && report.charts.length > 0) {
          console.log('WidgetBuilder - Setting charts from report data:', report.charts);
          setReportCharts(report.charts);
        } else {
          console.log('WidgetBuilder - No charts found in report data');
          setReportCharts([]);
        }
      }

      // Fetch actual report data for charts (same as ReportDataViewer)
      await fetchReportData(report);

    } catch (error) {
      console.error('Error loading report data:', error);
      setError('Error loading report data. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Fetch actual report data (same as ReportDataViewer)
  const fetchReportData = async (report, filters = {}) => {
    setPreviewLoading(true);
    console.log('WidgetBuilder - fetchReportData:', {
      reportId: report.id,
      limit: 10, // Limit to 10 records for widget preview
      filters: filters
    });

    try {
      // Use the same route as ReportDataViewer to fetch actual report data
      const dataApi = new DataApi('object');
      const response = await dataApi.post('/view/report', {
        reportId: report.id,
        limit: 10000, // Limit to 10 records for widget preview
        filters: filters
      });

      if (response?.data?.success) {
        const rawData = response.data.data;
        const grouping = response.data.grouping;

        console.log('WidgetBuilder - Report data loaded:', rawData);
        console.log('WidgetBuilder - Grouping info:', grouping);

        setPreviewData(rawData);
        setGroupingInfo(grouping);

      } else {
        throw new Error(response?.data?.message || 'Failed to fetch report data');
      }

    } catch (err) {
      console.error('WidgetBuilder - Error fetching report data:', err);
      setError('Error fetching report data. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const chartPreviewBundle = useMemo(
    () => {
      if (widgetType === 'dashboard') {
        return { dataset: {}, previewData: [], isMatrixReport: false };
      }
      return buildChartDatasetForPreview({
        rawData: previewData,
        groupingInfo,
        selectedFields,
        selectedGroups,
        selectedColumnGroups,
        summarizedFields,
        reportFields: loadedReportFields,
        reportConfig,
        reportCharts,
        chartConfig: selectedChart,
        baseCurrencyCode: reportConversionBaseCode,
        exchangeRateLookup: reportConversionLookup,
        multiCurrencyEnabled,
      });
    },
    [
      widgetType,
      previewData,
      groupingInfo,
      selectedFields,
      selectedGroups,
      selectedColumnGroups,
      summarizedFields,
      loadedReportFields,
      reportConfig,
      reportCharts,
      selectedChart,
      reportConversionBaseCode,
      reportConversionLookup,
      multiCurrencyEnabled,
    ]
  );

  const summarizedData = chartPreviewBundle.dataset || {};
  const chartPreviewData =
    chartPreviewBundle.previewData?.length > 0
      ? chartPreviewBundle.previewData
      : previewData;

  const transformChartForDisplay = (chart, summarizedDataForChart = null) =>
    normalizeReportChartForDisplay(chart, summarizedDataForChart);

  const handleChartSelect = async (chart) => {
    console.log('WidgetBuilder - Chart selected:', chart);
    console.log('WidgetBuilder - Current selectedFields:', selectedFields);
    console.log('WidgetBuilder - Current summarizedFields:', summarizedFields);
    console.log('WidgetBuilder - Current selectedGroups:', selectedGroups);

    setSelectedChart({ ...chart });
    setError(null);

    const chartConfig = normalizeReportChartForDisplay(
      { ...chart, title: chart.title },
      summarizedData
    );

    console.log('WidgetBuilder - Chart configuration being set:', {
      originalChart: chart,
      processedChartConfig: chartConfig,
      chartType: chartConfig.chartType,
      scatterXAxis: chartConfig.scatterXAxis,
      scatterYAxis: chartConfig.scatterYAxis,
      scatterGroupField: chartConfig.scatterGroupField
    });

    setWidgetConfig(prev => ({
      ...prev,
      name: prev.name || `${chart.title || 'Chart'} Widget`,
      chart_type: chartConfig.chartType,
      chart_config: chartConfig,
      type: 'chart'
    }));
  };

  const handleCreateNewChart = () => {
    setShowChartBuilder(true);
  };

  const handleChartCreated = (newChart) => {
    const normalized = normalizeReportChartForDisplay(newChart, summarizedData);
    setSelectedChart(normalized);
    setShowChartBuilder(false);
    setWidgetConfig(prev => ({
      ...prev,
      name: prev.name || `${newChart.title || 'Chart'} Widget`,
      chart_type: normalized.chartType || normalized.chart_type,
      chart_config: normalized,
      type: 'chart'
    }));
    setCurrentStep(getConfigStep('report', false));
  };

  const handleIncludeFullReportToggle = (value) => {
    setIncludeFullReport(value);

    if (value) {
      setSelectedChart(null);
      setWidgetConfig(prev => ({
        ...prev,
        chart_type: 'report',
        chart_config: null,
        type: 'report'
      }));
      setCurrentStep(3);
    } else {
      setWidgetConfig(prev => ({
        ...prev,
        chart_type: 'bar',
        chart_config: null,
        type: 'chart'
      }));
      setSelectedChart(null);
      setCurrentStep(3);
    }
  };

  const handleConfigChange = (field, value) => {
    setWidgetConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleKpiStepNext = () => {
    const validationError = getKpiConfigValidationError(widgetConfig.widget_config);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setCurrentStep(getConfigStep('kpi', false));
  };

  const handlePreview = async () => {
    try {
      setLoading(true);

      const isDashboardSelection = widgetType === 'dashboard';
      const isKpiSelection = widgetType === 'kpi';
      const isFullReportSelection = widgetType === 'report' && includeFullReport;
      const isChartSelection = widgetType === 'report' && !includeFullReport;

      if (isDashboardSelection && !selectedDashboard) {
        setError('Please select a dashboard before continuing.');
        setLoading(false);
        return;
      }

      if (
        isDashboardSelection &&
        dashboardId &&
        String(selectedDashboard?.id) === String(dashboardId)
      ) {
        setError('A dashboard cannot embed itself as a widget.');
        setLoading(false);
        return;
      }

      if (!isDashboardSelection && !selectedReport) {
        setError('Please select a report before continuing.');
        setLoading(false);
        return;
      }

      if (isChartSelection && !selectedChart) {
        setError('Please select a chart before continuing.');
        setLoading(false);
        return;
      }

      if (isKpiSelection) {
        const validationError = getKpiConfigValidationError(widgetConfig.widget_config);
        if (validationError) {
          setError(validationError);
          setLoading(false);
          return;
        }
      }

      const parentId = isDashboardSelection ? selectedDashboard?.id : selectedReport?.id;
      const reportLabel = selectedReport?.report_label || selectedReport?.name || 'Report';

      const defaultName = isDashboardSelection
        ? `${getDashboardDisplayLabel(selectedDashboard)} Widget`
        : isKpiSelection
          ? `${reportLabel} - KPI`
          : isFullReportSelection
            ? `${reportLabel} - Full Report`
            : (selectedChart?.title || `${reportLabel} - ${getChartTypeLabel(selectedChart?.chart_type || widgetConfig.chart_type)}`);

      const kpiLabel = isKpiSelection
        ? (
            (widgetConfig.widget_config || {}).label ||
            widgetConfig.name ||
            ''
          ).trim()
        : '';
      const widgetName = isKpiSelection
        ? kpiLabel || defaultName
        : widgetConfig.name || defaultName;

      const widgetPreviewData = {
        id: editingWidget ? editingWidget.id : `preview-${Date.now()}`,
        name: widgetName,
        type: isDashboardSelection
          ? 'dashboard'
          : isKpiSelection
            ? 'KPI'
            : isFullReportSelection
              ? 'report'
              : 'chart',
        parent_id: parentId,
        chart_type: isDashboardSelection
          ? 'dashboard'
          : isKpiSelection
            ? 'kpi'
            : isFullReportSelection
              ? 'report'
              : (selectedChart?.chartType || selectedChart?.chart_type || widgetConfig.chart_type),
        widget_config: isKpiSelection
          ? {
              ...(widgetConfig.widget_config || DEFAULT_KPI_WIDGET_CONFIG),
              label: widgetName,
            }
          : null,
        column_span: widgetConfig.column_span || 6,
        row_span: widgetConfig.row_span || 4,
        refresh_rate: widgetConfig.refresh_rate,
        show_filters: widgetConfig.show_filters,
        show_export: widgetConfig.show_export,
        sort_order: widgetConfig.sort_order,
        chart_id: isChartSelection ? selectedChart?.id : null,
        chart_config: isChartSelection ? (widgetConfig.chart_config || selectedChart || {}) : null,
        report_label: reportLabel,
        dashboard_label: isDashboardSelection
          ? (getDashboardDisplayLabel(selectedDashboard) || widgetName)
          : undefined,
        dashboard_description: isDashboardSelection ? selectedDashboard?.description : undefined,
        dataset: isChartSelection ? (editingWidget ? editingWidget.dataset : undefined) : null,
        previewData: isChartSelection ? (editingWidget ? (editingWidget.previewData || chartPreviewData) : chartPreviewData) : [],
        summarizedData: isChartSelection ? (editingWidget ? (editingWidget.summarizedData || editingWidget.dataset || summarizedData) : summarizedData) : null,
        groupingInfo: isChartSelection ? (editingWidget ? (editingWidget.groupingInfo || groupingInfo) : groupingInfo) : null,
        selectedFields: isChartSelection ? (editingWidget ? (editingWidget.selectedFields || selectedFields) : selectedFields) : [],
        summarizedFields: isChartSelection ? (editingWidget ? (editingWidget.summarizedFields || summarizedFields) : summarizedFields) : {},
        selectedGroups: isChartSelection ? (editingWidget ? (editingWidget.selectedGroups || selectedGroups) : selectedGroups) : [],
        selectedColumnGroups: isChartSelection
          ? (editingWidget ? (editingWidget.selectedColumnGroups || selectedColumnGroups) : selectedColumnGroups)
          : [],
        isPreview: !editingWidget,
        x: editingWidget ? editingWidget.x : 0,
        y: editingWidget ? editingWidget.y : 0,
        selectedChart: isChartSelection ? selectedChart : null,
        selectedDashboard: isDashboardSelection ? cloneDashboardSelection(selectedDashboard) : null,
        selectedReport: isDashboardSelection ? null : (selectedReport ? { ...selectedReport } : null)
      };

      if (onPreviewWidget) {
        await onPreviewWidget(widgetPreviewData);
      }

      onClose();

    } catch (error) {
      console.error('WidgetBuilder - Error creating/updating widget:', error);
      setError('Failed to create/update widget');
    } finally {
      setLoading(false);
    }
  };

  const handleWidgetTypeSelect = (type) => {
    setWidgetType(type);
    setSelectedDashboard(null);
    setError(null);
    setSearchTerm('');
    setDashboardSearchTerm('');
    if (type === 'dashboard') {
      clearReportPreviewState();
    } else {
      setSelectedReport(null);
      setSelectedChart(null);
    }
    setWidgetConfig(prev => ({
      ...prev,
      name: '',
      chart_type: type === 'dashboard' ? 'dashboard' : type === 'kpi' ? 'kpi' : 'bar',
      chart_config: type === 'dashboard' || type === 'kpi' ? null : {},
      widget_config: type === 'kpi' ? { ...DEFAULT_KPI_WIDGET_CONFIG } : prev.widget_config,
      type: type === 'dashboard' ? 'dashboard' : type === 'kpi' ? 'kpi' : 'report'
    }));
    if (type === 'dashboard') {
      setIncludeFullReport(false);
      if (dashboards.length === 0) {
        loadDashboards();
      }
    } else {
      setIncludeFullReport(false);
      if (reports.length === 0) {
        loadReports();
      }
    }
    setCurrentStep(2);
  };

  const handleDashboardSelect = (dashboard) => {
    if (dashboardId && String(dashboard.id) === String(dashboardId)) {
      setError('A dashboard cannot embed itself as a widget.');
      return;
    }
    setSelectedDashboard(cloneDashboardSelection(dashboard));
    clearReportPreviewState();
    setError(null);
    setWidgetConfig(prev => ({
      ...prev,
      name: prev.name || `${getDashboardDisplayLabel(dashboard)} Widget`,
      chart_type: 'dashboard',
      chart_config: null,
      type: 'dashboard'
    }));
    setCurrentStep(3);
  };


  const getChartIcon = (chartType) => getChartTypeDisplayIcon(chartType);

  const getChartTypeLabel = (chartType) => getChartTypeDisplayLabel(chartType);

  const renderChartSelectionStep = () => (
    <div className="builder-step">
      <div className="step-header">
        {!editingWidget && (
          <button
            className="back-btn"
            onClick={() => setCurrentStep(2)}
          >
            <FaArrowLeft />
            Back
          </button>
        )}
        <h3>Choose Chart Type</h3>
        <p>Select an existing chart or create a new one</p>
      </div>

      {selectedReport && (
        <div className="selected-report">
          <div className="report-summary">
            <FaChartBar className="report-icon" />
            <div>
              <h4>{selectedReport.report_label || selectedReport.name}</h4>
              <p>Selected Report</p>
            </div>
          </div>
        </div>
      )}

      {widgetType === 'report' && (
        <div className="widget-report-options">
          <div className="widget-report-mode-toggle">
            <button
              type="button"
              className={`widget-report-mode-btn ${includeFullReport ? 'active' : ''}`}
              onClick={() => handleIncludeFullReportToggle(true)}
            >
              <span className="widget-report-mode-title">Full Report</span>
              <span className="widget-report-mode-subtitle">Embed complete report viewer</span>
            </button>
            <button
              type="button"
              className={`widget-report-mode-btn ${!includeFullReport ? 'active' : ''}`}
              onClick={() => handleIncludeFullReportToggle(false)}
            >
              <span className="widget-report-mode-title">Specific Chart</span>
              <span className="widget-report-mode-subtitle">Pick from report charts</span>
            </button>
          </div>

          {includeFullReport && (
            <p className="widget-report-help-text">
              The widget will render the entire report using the standard report viewer. Sort order and sizing can be adjusted in the next step.
            </p>
          )}
        </div>
      )}

      {!includeFullReport && (
        <div className="chart-options">
          <div className="existing-charts">
            <h4>Existing Charts</h4>
            {reportCharts.length === 0 ? (
              <div className="no-charts">
                <p>No existing charts found for this report</p>
              </div>
            ) : (
              <div className="charts-list">
                {reportCharts.map((chart) => (
                  <div
                    key={chart.id}
                    className="chart-preview-item"
                    onClick={() => handleChartSelect(chart)}
                  >
                    <div className="chart-preview-header">
                      <div className="chart-icon">
                        {getChartIcon(chart.chart_type)}
                      </div>
                      <div className="chart-info">
                        <h5>{chart.title || 'Untitled Chart'}</h5>
                        <p>{getChartTypeLabel(chart.chart_type)}</p>
                      </div>
                      <div className="chart-select-btn">
                        <FaArrowRight />
                      </div>
                    </div>
                    <div className="chart-preview-content">
                      {previewLoading ? (
                        <div className="loading-state">
                          <div className="loading-spinner"></div>
                          <p>Loading chart data...</p>
                        </div>
                      ) : previewData.length > 0 && summarizedData && Object.keys(summarizedData).length > 0 ? (
                        <div className="chart-preview">
                          <ChartRenderer
                            chart={transformChartForDisplay(chart, summarizedData)}
                            selectedFields={selectedFields}
                            summarizedFields={summarizedFields}
                            selectedGroups={selectedGroups}
                            selectedColumnGroups={selectedColumnGroups}
                            summarizedData={summarizedData}
                            previewData={chartPreviewData}
                            baseCurrencyCode={reportConversionBaseCode}
                            exchangeRateLookup={reportConversionLookup}
                            multiCurrencyEnabled={multiCurrencyEnabled}
                            isPreview={true}
                            displayAuditInfo={false}
                          />
                        </div>
                      ) : (
                        <div className="chart-preview-placeholder">
                          <div className="placeholder-icon">
                            {getChartIcon(chart.chart_type)}
                          </div>
                          <p>Chart Preview</p>
                          <small>{chart.title || 'Untitled Chart'}</small>
                          <div className="chart-config-info">
                            <div className="config-item">
                              <strong>Type:</strong> {getChartTypeLabel(chart.chart_type)}
                            </div>
                            {chart.x_axis_field && (
                              <div className="config-item">
                                <strong>X-Axis:</strong> {chart.x_axis_field}
                              </div>
                            )}
                            {chart.y_axis_field && (
                              <div className="config-item">
                                <strong>Y-Axis:</strong> {chart.y_axis_field}
                              </div>
                            )}
                            {chart.group_field && (
                              <div className="config-item">
                                <strong>Group:</strong> {chart.group_field}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderConfigStep = () => {
    const backTarget = widgetType === 'dashboard' ? 2 : widgetType === 'report' && includeFullReport ? 2 : 3;
    const showSelectedChartSummary = !includeFullReport && selectedChart && (selectedChart.chart_type || selectedChart.chartType);
    const columnOptions = widgetType === 'report'
      ? REPORT_COLUMN_OPTIONS
      : DASHBOARD_COLUMN_VALUES.map((value) => ({
          value,
          label: getDashboardColumnLabel(value)
        }));

    return (
      <div className="builder-step">
        <div className="step-header">
          {!editingWidget && (
            <button
              className="back-btn"
              onClick={() => setCurrentStep(backTarget)}
            >
              <FaArrowLeft />
              Back
            </button>
          )}
          <h3>{editingWidget ? 'Edit Widget' : 'Configure Widget'}</h3>
          <p>{editingWidget ? 'Modify the widget settings' : 'Set the widget size, name, and display options'}</p>
        </div>

        {widgetType === 'dashboard' && selectedDashboard && (
          <div className="selected-report">
            <div className="report-summary">
              <FaTachometerAlt className="report-icon" />
              <div>
                <h4>{getDashboardDisplayLabel(selectedDashboard)}</h4>
                <p>Selected Dashboard</p>
              </div>
            </div>
          </div>
        )}

        {widgetType === 'report' && includeFullReport && selectedReport && (
          <div className="full-report-summary">
            <div className="report-summary">
              <FaRegFileAlt className="report-icon" />
              <div>
                <h4>{selectedReport.report_label || selectedReport.name}</h4>
                <p>The full report viewer will be embedded in this widget.</p>
              </div>
            </div>
          </div>
        )}

        {showSelectedChartSummary && (() => {
          const chartType = (selectedChart.chartType || selectedChart.chart_type || '').toLowerCase();
          const chartName = selectedChart.name || `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart`;
          return (
            <div className="selected-chart">
              <div className="chart-summary">
                <div className="chart-icon">
                  {chartType === 'bar' && <FaChartBar />}
                  {chartType === 'line' && <FaChartLine />}
                  {chartType === 'pie' && <FaChartPie />}
                  {chartType === 'area' && <FaChartArea />}
                  {chartType === 'table' && <FaTable />}
                </div>
                <div>
                  <h4>{chartName}</h4>
                  <p>Selected Chart</p>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="widget-config-form">
          <div className="config-section">
            <h4>Widget Settings</h4>

            <div className="form-group">
              <label htmlFor="widget-name">Widget Name</label>
              <input
                id="widget-name"
                type="text"
                value={widgetConfig.name}
                onChange={(e) => handleConfigChange('name', e.target.value)}
                placeholder="Enter widget name..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="column-span">Columns (Width)</label>
              <select
                id="column-span"
                value={widgetConfig.column_span}
                onChange={(e) => handleConfigChange('column_span', parseInt(e.target.value))}
              >
                {columnOptions.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="row-span">Rows (Height)</label>
              <select
                id="row-span"
                value={widgetConfig.row_span}
                onChange={(e) => handleConfigChange('row_span', parseInt(e.target.value))}
              >
                {ROW_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {getRowOptionLabel(value)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="sort-order">Sort Order</label>
              <input
                id="sort-order"
                type="number"
                min="1"
                value={widgetConfig.sort_order || ''}
                onChange={(e) => handleConfigChange('sort_order', parseInt(e.target.value) || '')}
                placeholder="Enter sort order number..."
              />
            </div>
          </div>

          <div className="config-actions">
            <button
              className="add-widget-btn"
              onClick={handlePreview}
              disabled={loading || (widgetType === 'dashboard' && !selectedDashboard) || (widgetType === 'report' && !includeFullReport && !selectedChart)}
            >
              {loading ? (
                <>
                  <div className="loading-spinner-small"></div>
                  {editingWidget ? 'Updating Widget...' : 'Adding Widget...'}
                </>
              ) : (
                <>
                  <FaPlus />
                  {editingWidget ? 'Update Widget' : 'Add Widget'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const handleFooterBack = () => {
    if (editingWidget || currentStep <= 1) return;

    if (currentStep === 2) {
      setCurrentStep(1);
      setSelectedDashboard(null);
      setSelectedReport(null);
      setSearchTerm('');
      setDashboardSearchTerm('');
      return;
    }

    const configStep = getConfigStep(widgetType, includeFullReport);

    if (currentStep === configStep) {
      if (widgetType === 'dashboard') {
        setSelectedDashboard(null);
        setCurrentStep(2);
      } else if (widgetType === 'kpi') {
        setCurrentStep(3);
      } else if (widgetType === 'report') {
        setCurrentStep(3);
      }
      return;
    }

    if (currentStep === 3) {
      if (widgetType === 'dashboard') {
        setSelectedDashboard(null);
        setCurrentStep(2);
      } else {
        setSelectedReport(null);
        setSelectedChart(null);
        setIncludeFullReport(false);
        setCurrentStep(2);
      }
    }
  };

  const showFooterBack = !editingWidget && currentStep > 1;

  const steps =
    widgetType === 'report'
      ? ['Type', 'Select', 'Chart', 'Config']
      : widgetType === 'kpi'
        ? ['Type', 'Select', 'KPI', 'Config']
        : ['Type', 'Select', 'Config'];
  const configStepIndex = steps.length;
  const headerTitle = editingWidget ? 'Edit Widget' : 'Add Widget';

  return (
    <div className="widget-builder-overlay">
      <div className="widget-builder">
        {/* Header */}
        <div className="builder-header">
          <div className="builder-title">
            <h2>{headerTitle}</h2>
            <button className="builder-close-btn" onClick={onClose} aria-label="Close widget builder">
              ×
            </button>
          </div>
          <div className="builder-steps">
            {steps.map((label, index) => (
              <div key={label} className={`step ${currentStep >= index + 1 ? 'active' : ''}`}>
                <span className="step-number">{index + 1}</span>
                <span className="step-label">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="builder-content">
          {error && (
            <div className="builder-error">
              <p>{error}</p>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {/* Step 1: Choose Widget Type */}
          {currentStep === 1 && (
            <div className="builder-step">
              <div className="step-header">
                <h3>Choose Widget Type</h3>
                <p>Select the type of widget you want to add to the dashboard</p>
              </div>
              <div className="widget-type-options">
                <button
                  type="button"
                  className={`widget-type-option ${widgetType === 'report' ? 'active' : ''}`}
                  onClick={() => handleWidgetTypeSelect('report')}
                >
                  <div className="widget-type-icon report">
                    <FaChartBar />
                  </div>
                  <div className="widget-type-content">
                    <h4>Report Widget</h4>
                    <p>Embed a full report or one of its charts</p>
                  </div>
                </button>
                <button
                  type="button"
                  className={`widget-type-option ${widgetType === 'kpi' ? 'active' : ''}`}
                  onClick={() => handleWidgetTypeSelect('kpi')}
                >
                  <div className="widget-type-icon kpi">
                    <FaTachometerAlt />
                  </div>
                  <div className="widget-type-content">
                    <h4>KPI Widget</h4>
                    <p>Single metric from a report with optional period comparison</p>
                  </div>
                </button>
                <button
                  type="button"
                  className={`widget-type-option ${widgetType === 'dashboard' ? 'active' : ''}`}
                  onClick={() => handleWidgetTypeSelect('dashboard')}
                >
                  <div className="widget-type-icon dashboard">
                    <FaChartPie />
                  </div>
                  <div className="widget-type-content">
                    <h4>Dashboard Widget</h4>
                    <p>Embed an existing dashboard inside this dashboard</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Select Source */}
          {currentStep === 2 && widgetType === 'dashboard' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={handleFooterBack}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Choose a Dashboard</h3>
                <p>Select the dashboard that will be displayed as a widget</p>
              </div>

              <div className="search-section">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search dashboards..."
                    value={dashboardSearchTerm}
                    onChange={(e) => setDashboardSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="reports-list">
                {dashboardsLoading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboards...</p>
                  </div>
                ) : filteredDashboards.length === 0 ? (
                  <div className="empty-state">
                    <p>No dashboards found</p>
                  </div>
                ) : (
                  filteredDashboards.map((dashboard) => (
                    <div
                      key={dashboard.id}
                      className={`report-item ${selectedDashboard?.id === dashboard.id ? 'selected' : ''}`}
                      onClick={() => handleDashboardSelect(dashboard)}
                    >
                      <div className="report-icon dashboard">
                        <FaChartBar />
                      </div>
                      <div className="report-info">
                        <h4>{getDashboardDisplayLabel(dashboard)}</h4>
                        <p>Dashboard</p>
                      </div>
                      <div className="report-meta">
                        {dashboard.description && <span>{dashboard.description}</span>}
                      </div>
                      <div className="report-arrow">
                        <FaArrowRight />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {currentStep === 2 && (widgetType === 'report' || widgetType === 'kpi') && (
            <div className="builder-step">
              <div className="step-header">
                {!editingWidget && (
                  <button
                    className="back-btn"
                    onClick={handleFooterBack}
                  >
                    <FaArrowLeft />
                    Back
                  </button>
                )}
                <h3>Choose a Report</h3>
                <p>
                  {widgetType === 'kpi'
                    ? 'Select the report that defines filters, joins, and security for this KPI'
                    : 'Select the report that will provide data for your widget'}
                </p>
              </div>

              <div className="search-section">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search reports..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="reports-list">
                {loading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading reports...</p>
                  </div>
                ) : filteredReports.length === 0 ? (
                  <div className="empty-state">
                    <p>No reports found</p>
                  </div>
                ) : (
                  filteredReports.map((report) => (
                    <div
                      key={report.id}
                      className={`report-item ${selectedReport?.id === report.id ? 'selected' : ''}`}
                      onClick={() => handleReportSelect(report)}
                    >
                      <div className="report-icon">
                        <FaChartBar />
                      </div>
                      <div className="report-info">
                        <h4>{report.report_label || report.name}</h4>
                        <p>{report.description || 'Report'}</p>
                      </div>
                      <div className="report-arrow">
                        <FaArrowRight />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 3: KPI configuration */}
          {currentStep === 3 && widgetType === 'kpi' && (
            <div className="builder-step">
              <div className="step-header">
                <button className="back-btn" onClick={() => setCurrentStep(2)}>
                  <FaArrowLeft />
                  Back
                </button>
                <h3>KPI Configuration</h3>
                <p>Define the metric, aggregation, and comparison settings</p>
              </div>

              {selectedReport && (
                <div className="selected-report">
                  <p>
                    <strong>Report:</strong> {selectedReport.report_label || selectedReport.name}
                  </p>
                </div>
              )}

              <div className="widget-config-form kpi-config-form">
                <div className="config-group">
                  <label htmlFor="kpi-widget-label">Label</label>
                  <input
                    id="kpi-widget-label"
                    type="text"
                    value={(widgetConfig.widget_config || {}).label ?? ''}
                    onChange={(e) => handleKpiConfigChange('label', e.target.value)}
                    placeholder="e.g. Total Revenue"
                  />
                  <p className="config-hint">Shown as the widget title on the dashboard.</p>
                </div>
                <div className="config-group">
                  <label>Metric field</label>
                  <select
                    value={(widgetConfig.widget_config || {}).metric_field || ''}
                    onChange={(e) => handleKpiConfigChange('metric_field', e.target.value)}
                    disabled={previewLoading}
                  >
                    <option value="">
                      {previewLoading
                        ? 'Loading report fields...'
                        : kpiNumericFields.length === 0
                          ? 'No fields available on this report'
                          : 'Select field...'}
                    </option>
                    {kpiNumericFields.map((f) => (
                      <option key={`${f.object_name}_${f.field_name}`} value={f.field_name}>
                        {f.field_label || f.field_name}
                        {f.object_name ? ` (${f.object_name})` : ''}
                      </option>
                    ))}
                  </select>
                  {!previewLoading && kpiNumericFields.length === 0 && selectedReport && (
                    <p className="config-hint">
                      Add a number, currency, or summarized amount field to the report first.
                    </p>
                  )}
                </div>
                <div className="config-group">
                  <label>Aggregation</label>
                  <select
                    value={(widgetConfig.widget_config || {}).aggregation || 'SUM'}
                    onChange={(e) => handleKpiConfigChange('aggregation', e.target.value)}
                  >
                    {KPI_AGGREGATIONS.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="config-group">
                  <label>Comparison</label>
                  <select
                    value={(widgetConfig.widget_config || {}).comparison || 'NONE'}
                    onChange={(e) => handleKpiConfigChange('comparison', e.target.value)}
                  >
                    {KPI_COMPARISONS.map((c) => (
                      <option key={c} value={c}>
                        {c.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="config-group">
                  <label>Date field (for comparison)</label>
                  <select
                    value={(widgetConfig.widget_config || {}).date_field || ''}
                    onChange={(e) => handleKpiConfigChange('date_field', e.target.value)}
                  >
                    <option value="">None</option>
                    {kpiDateFields.map((f) => (
                      <option key={`${f.object_name}_${f.field_name}`} value={f.field_name}>
                        {f.field_label || f.field_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="config-group">
                  <label>Format</label>
                  <select
                    value={(widgetConfig.widget_config || {}).format || 'number'}
                    onChange={(e) => handleKpiConfigChange('format', e.target.value)}
                  >
                    {KPI_FORMATS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="config-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={(widgetConfig.widget_config || {}).show_trend !== false}
                      onChange={(e) => handleKpiConfigChange('show_trend', e.target.checked)}
                    />
                    Show trend %
                  </label>
                </div>

                <div className="config-section kpi-appearance-section">
                  <h4>Appearance</h4>
                  <div className="config-group kpi-color-row">
                    <label htmlFor="kpi-bg-color">Background color</label>
                    <input
                      id="kpi-bg-color"
                      type="color"
                      value={
                        (widgetConfig.widget_config || {}).background_color ||
                        '#ffffff'
                      }
                      onChange={(e) =>
                        handleKpiConfigChange('background_color', e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="kpi-color-reset-btn"
                      onClick={() =>
                        handleKpiConfigChange(
                          'background_color',
                          KPI_APPEARANCE_DEFAULTS.background_color
                        )
                      }
                    >
                      Default (transparent)
                    </button>
                  </div>
                  <div className="config-group kpi-color-row">
                    <label htmlFor="kpi-title-color">Title color</label>
                    <input
                      id="kpi-title-color"
                      type="color"
                      value={
                        (widgetConfig.widget_config || {}).title_color ||
                        KPI_APPEARANCE_DEFAULTS.title_color
                      }
                      onChange={(e) =>
                        handleKpiConfigChange('title_color', e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="kpi-color-reset-btn"
                      onClick={() =>
                        handleKpiConfigChange(
                          'title_color',
                          KPI_APPEARANCE_DEFAULTS.title_color
                        )
                      }
                    >
                      Default
                    </button>
                  </div>
                  <div className="config-group kpi-color-row">
                    <label htmlFor="kpi-value-color">Main value color ($ amount)</label>
                    <input
                      id="kpi-value-color"
                      type="color"
                      value={
                        (widgetConfig.widget_config || {}).value_color ||
                        KPI_APPEARANCE_DEFAULTS.value_color
                      }
                      onChange={(e) =>
                        handleKpiConfigChange('value_color', e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="kpi-color-reset-btn"
                      onClick={() =>
                        handleKpiConfigChange(
                          'value_color',
                          KPI_APPEARANCE_DEFAULTS.value_color
                        )
                      }
                    >
                      Default
                    </button>
                  </div>
                  <div className="config-group kpi-color-row">
                    <label htmlFor="kpi-positive-color">Positive trend color (up arrow only)</label>
                    <input
                      id="kpi-positive-color"
                      type="color"
                      value={
                        (widgetConfig.widget_config || {}).positive_color ||
                        KPI_APPEARANCE_DEFAULTS.positive_color
                      }
                      onChange={(e) =>
                        handleKpiConfigChange('positive_color', e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="kpi-color-reset-btn"
                      onClick={() =>
                        handleKpiConfigChange(
                          'positive_color',
                          KPI_APPEARANCE_DEFAULTS.positive_color
                        )
                      }
                    >
                      Default
                    </button>
                  </div>
                  <div className="config-group kpi-color-row">
                    <label htmlFor="kpi-negative-color">Negative trend color (down arrow only)</label>
                    <input
                      id="kpi-negative-color"
                      type="color"
                      value={
                        (widgetConfig.widget_config || {}).negative_color ||
                        KPI_APPEARANCE_DEFAULTS.negative_color
                      }
                      onChange={(e) =>
                        handleKpiConfigChange('negative_color', e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="kpi-color-reset-btn"
                      onClick={() =>
                        handleKpiConfigChange(
                          'negative_color',
                          KPI_APPEARANCE_DEFAULTS.negative_color
                        )
                      }
                    >
                      Default
                    </button>
                  </div>
                  <p className="config-hint">
                    Main value color controls $4.8K-style numbers. Positive/negative colors
                    apply only when the trend arrow points up or down. Clear background for
                    a transparent tile.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Report presentation options */}
          {currentStep === 3 && widgetType === 'report' && (
            <div className="builder-step">
              <div className="step-header">
                <button
                  className="back-btn"
                  onClick={() => setCurrentStep(2)}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <h3>Choose Widget Content</h3>
                <p>Embed the full report viewer or pick a specific chart</p>
              </div>

              {selectedReport && (
                <div className="selected-report">
                  <div className="report-summary">
                    <FaChartBar className="report-icon" />
                    <div>
                      <h4>{selectedReport.report_label || selectedReport.name}</h4>
                      <p>Selected Report</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="widget-report-options">
                <div className="widget-report-mode-toggle">
                  <button
                    type="button"
                    className={`widget-report-mode-btn ${includeFullReport ? 'active' : ''}`}
                    onClick={() => handleIncludeFullReportToggle(true)}
                  >
                    <span className="widget-report-mode-title">Full Report</span>
                    <span className="widget-report-mode-subtitle">Embed complete report viewer</span>
                  </button>
                  <button
                    type="button"
                    className={`widget-report-mode-btn ${!includeFullReport ? 'active' : ''}`}
                    onClick={() => handleIncludeFullReportToggle(false)}
                  >
                    <span className="widget-report-mode-title">Specific Chart</span>
                    <span className="widget-report-mode-subtitle">Pick from report charts</span>
                  </button>
                </div>

                {includeFullReport && (
                  <p className="widget-report-help-text">
                    The widget will render the entire report using the standard report viewer. Sort order and sizing can be adjusted in the next step.
                  </p>
                )}

                {!includeFullReport && (
                  <div className="chart-options">
                    <div className="existing-charts">
                      <h4>Existing Charts</h4>
                      {reportCharts.length === 0 ? (
                        <div className="no-charts">
                          <p>No existing charts found for this report</p>
                        </div>
                      ) : (
                        <div className="charts-list">
                          {reportCharts.map((chart) => (
                            <div
                              key={chart.id}
                              className={`chart-preview-item ${selectedChart?.id === chart.id ? 'selected' : ''}`}
                              onClick={() => handleChartSelect(chart)}
                            >
                              <div className="chart-preview-header">
                                <div className="chart-icon">
                                  {getChartIcon(chart.chart_type)}
                                </div>
                                <div className="chart-info">
                                  <h5>{chart.title || 'Untitled Chart'}</h5>
                                  <p>{getChartTypeLabel(chart.chart_type)}</p>
                                </div>
                                <div className="chart-select-btn">
                                  <FaArrowRight />
                                </div>
                              </div>
                              <div className="chart-preview-content">
                                {previewLoading ? (
                                  <div className="loading-state">
                                    <div className="loading-spinner"></div>
                                    <p>Loading chart data...</p>
                                  </div>
                                ) : previewData.length > 0 && summarizedData && Object.keys(summarizedData).length > 0 ? (
                                  <div className="chart-preview">
                                    <ChartRenderer
                                      chart={transformChartForDisplay(chart, summarizedData)}
                                      selectedFields={selectedFields}
                                      summarizedFields={summarizedFields}
                                      selectedGroups={selectedGroups}
                                      selectedColumnGroups={selectedColumnGroups}
                                      summarizedData={summarizedData}
                                      previewData={chartPreviewData}
                                      baseCurrencyCode={reportConversionBaseCode}
                                      exchangeRateLookup={reportConversionLookup}
                                      multiCurrencyEnabled={multiCurrencyEnabled}
                                      isPreview={true}
                                      displayAuditInfo={false}
                                    />
                                  </div>
                                ) : (
                                  <div className="chart-preview-placeholder">
                                    <div className="placeholder-icon">
                                      {getChartIcon(chart.chart_type)}
                                    </div>
                                    <p>Chart Preview</p>
                                    <small>{chart.title || 'Untitled Chart'}</small>
                                    <div className="chart-config-info">
                                      <div className="config-item">
                                        <strong>Type:</strong> {getChartTypeLabel(chart.chart_type)}
                                      </div>
                                      {chart.x_axis_field && (
                                        <div className="config-item">
                                          <strong>X-Axis:</strong> {chart.x_axis_field}
                                        </div>
                                      )}
                                      {chart.y_axis_field && (
                                        <div className="config-item">
                                          <strong>Y-Axis:</strong> {chart.y_axis_field}
                                        </div>
                                      )}
                                      {chart.group_field && (
                                        <div className="config-item">
                                          <strong>Group:</strong> {chart.group_field}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

            </div>
          )}

          {/* Step 3 or 4: Widget Configuration */}
          {currentStep === configStepIndex && (
            <div className="builder-step">
              <div className="step-header">
                {!editingWidget && currentStep > 1 && (
                  <button
                    className="back-btn"
                    onClick={() => setCurrentStep(Math.max(1, configStepIndex - 1))}
                  >
                    <FaArrowLeft />
                    Back
                  </button>
                )}
                <h3>{editingWidget ? 'Edit Widget' : 'Configure Widget'}</h3>
                <p>{editingWidget ? 'Modify the widget settings' : 'Set the widget size, name, and display options'}</p>
              </div>

              {widgetType === 'kpi' && selectedReport && (
                <div className="full-report-summary">
                  <div className="report-summary">
                    <FaTachometerAlt className="report-icon" />
                    <div>
                      <h4>{selectedReport.report_label || selectedReport.name}</h4>
                      <p>
                        Metric: {(widgetConfig.widget_config || {}).metric_field || '—'} (
                        {(widgetConfig.widget_config || {}).aggregation || 'SUM'})
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {widgetType === 'dashboard' && selectedDashboard && (
                <div className="selected-chart">
                  <div className="chart-summary">
                    <div className="chart-icon">
                      <FaChartBar />
                    </div>
                    <div>
                      <h4>{getDashboardDisplayLabel(selectedDashboard)}</h4>
                      <p>Selected Dashboard</p>
                    </div>
                  </div>
                </div>
              )}

              {widgetType === 'report' && includeFullReport && selectedReport && (
                <div className="full-report-summary">
                  <div className="report-summary">
                    <FaChartBar className="report-icon" />
                    <div>
                      <h4>{selectedReport.report_label || selectedReport.name}</h4>
                      <p>The full report viewer will be embedded in this widget.</p>
                    </div>
                  </div>
                </div>
              )}

              {widgetType === 'report' && !includeFullReport && selectedChart && (
                <div className="selected-chart">
                  <div className="chart-summary">
                    <div className="chart-icon">
                      {selectedChart.chartType === 'bar' && <FaChartBar />}
                      {selectedChart.chartType === 'line' && <FaChartLine />}
                      {selectedChart.chartType === 'pie' && <FaChartPie />}
                      {selectedChart.chartType === 'area' && <FaChartArea />}
                      {selectedChart.chartType === 'table' && <FaTable />}
                    </div>
                    <div>
                      <h4>{selectedChart.name || `${selectedChart.chartType.charAt(0).toUpperCase() + selectedChart.chartType.slice(1)} Chart`}</h4>
                      <p>Selected Chart</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="widget-config-form">
                <div className="config-section">
                  <h4>Widget Settings</h4>

                  {widgetType === 'kpi' ? (
                    <div className="form-group">
                      <label htmlFor="kpi-widget-label-step4">Label</label>
                      <input
                        id="kpi-widget-label-step4"
                        type="text"
                        value={(widgetConfig.widget_config || {}).label ?? ''}
                        onChange={(e) => handleKpiConfigChange('label', e.target.value)}
                        placeholder="e.g. Total Revenue"
                      />
                    </div>
                  ) : (
                    <div className="form-group">
                      <label htmlFor="widget-name">Widget Name</label>
                      <input
                        id="widget-name"
                        type="text"
                        value={widgetConfig.name}
                        onChange={(e) => handleConfigChange('name', e.target.value)}
                        placeholder="Enter widget name..."
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="column-span">Columns (Width)</label>
                    <select
                      id="column-span"
                      value={widgetConfig.column_span}
                      onChange={(e) => handleConfigChange('column_span', parseInt(e.target.value))}
                    >
                      {DASHBOARD_COLUMN_VALUES.map((value) => (
                        <option key={value} value={value}>
                          {getDashboardColumnLabel(value)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="row-span">Rows (Height)</label>
                    <select
                      id="row-span"
                      value={widgetConfig.row_span}
                      onChange={(e) => handleConfigChange('row_span', parseInt(e.target.value))}
                    >
                      {ROW_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                          {getRowOptionLabel(value)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="sort-order">Sort Order</label>
                    <input
                      id="sort-order"
                      type="number"
                      min="1"
                      value={widgetConfig.sort_order || ''}
                      onChange={(e) => handleConfigChange('sort_order', parseInt(e.target.value) || '')}
                      placeholder="Enter sort order number..."
                    />
                  </div>
                </div>

                <div className="config-actions">
                  <button
                    className="add-widget-btn"
                    onClick={handlePreview}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        {editingWidget ? 'Updating Widget...' : 'Adding Widget...'}
                      </>
                    ) : (
                      <>
                        <FaPlus />
                        {editingWidget ? 'Update Widget' : 'Add Widget'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="builder-footer">
          <div className="footer-actions">
            <div className="footer-buttons">
              <button
                className="cancel-btn"
                onClick={onClose}
              >
                Cancel
              </button>
              {showFooterBack && (
                <button
                  type="button"
                  className="footer-back-btn"
                  onClick={handleFooterBack}
                >
                  <FaArrowLeft />
                  Back
                </button>
              )}
              {currentStep === 3 && widgetType === 'report' && (
                <button
                  className="footer-next-btn"
                  onClick={() => setCurrentStep(configStepIndex)}
                  disabled={!includeFullReport && !selectedChart}
                >
                  Next
                  <FaArrowRight />
                </button>
              )}
              {currentStep === 3 && widgetType === 'kpi' && (
                <button
                  type="button"
                  className="footer-next-btn"
                  onClick={handleKpiStepNext}
                >
                  Next
                  <FaArrowRight />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetBuilder;
